import window from '@atlas/tool/box/browser/window';
import fileResponseToUploadedAttachment from '@atlas/tool/box/file/fileResponseToUploadedAttachment';
import type { TUploadedAttachment } from '@atlas/tool/box/file/types';
import filestack, { basePickerOptions, type TFileStackPickerOptions } from '@atlas/tool/box/file/filestack';
import notify from '@atlas/tool/box/ui/notify';

export default async function uploadFiles(
    options: TUploadFilesOptions = {},
): Promise<undefined | TUploadedAttachment[]> {
    options = { ...options, ...basePickerOptions };
    const { filename, ...fsOptions } = options;

    const client = await filestack();

    return new Promise((resolve, reject) => {
        const modal = getModalControls();

        const fsPickerInstance = client.picker({
            ...fsOptions,

            container: containerSelector,
            onOpen: () => {
                modal.open();
                fsOptions.onOpen?.();
            },
            onUploadDone: async (res) => {
                modal.close();

                if (res.filesFailed.length) {
                    const message = 'Uploading file failed';
                    reject(new Error(message));
                }

                await fsOptions.onUploadDone?.(res);
                resolve(res.filesUploaded.map(fileResponseToUploadedAttachment));
            },
            onCancel: () => {
                resolve(undefined);
                modal.close();
                fsOptions.onCancel?.();
            },
            onFileSelected: (file) => {
                const normalizedFile = {
                    ...file,
                    name: !filename ? file.filename : transferFileExtension(file.filename, filename),
                };
                return fsOptions.onFileSelected?.(normalizedFile) || normalizedFile;
            },
            onFileUploadFailed: (file) => {
                // Filestack doesn't provide an error reason, so if the file is bigger than 30MB, we can assume that's the reason
                if (file.size > MAX_FILE_SIZE) {
                    notify.error({ message: 'File size is bigger than 30MB' });
                    return;
                }

                notify.error({ message: 'Uploading file failed' });
            },
        });

        fsPickerInstance.open();
    });
}

// #region Helpers

function retryFilestackModalMounted(element: HTMLElement, cb: () => void, retries = 10) {
    if (element.querySelector('.fsp-modal')) return cb();
    setTimeout(() => retryFilestackModalMounted(element, cb, retries - 1), 20);
}

export const filestackContainerId = 'atlas-filestack-dialog';

const containerSelector = `#${filestackContainerId}`;

function getModalControls() {
    const existingContainer = window?.document.querySelector<HTMLDialogElement>(containerSelector);

    const newContainer = existingContainer ? undefined : document.createElement('dialog');
    if (newContainer) {
        newContainer.id = filestackContainerId;
        Object.assign(newContainer.style, { border: 'none', background: 'transparent' });
        window?.document.body.appendChild(newContainer);
    }

    const element = existingContainer || newContainer;
    const open = () => element && retryFilestackModalMounted(element, () => element.showModal());
    const close = () => element?.close();

    return { open, close };
}

function transferFileExtension(from: string, to: string) {
    const extension = from.split('.').pop();
    if (!extension) return to;
    return `${to}.${extension}`;
}

export const MAX_FILE_SIZE = 30 * 1024 * 1024;

// #endregion

// #region Types

export type TUploadFilesOptions = TFileStackPickerOptions & { filename?: string };

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, test, expect, vi, beforeEach, afterEach } = import.meta.vitest;
    describe('uploadFiles', () => {
        if (!window) return;
        let createElement: typeof window.document.body.appendChild;
        let appendChild: typeof window.document.createElement;

        beforeEach(() => {
            if (!window) return;
            vi.mock('@atlas/tool/box/file/filestack', () => {
                const pickerOpenMock = vi.fn().mockResolvedValue(undefined);
                const pickerMock = vi.fn().mockImplementation((options: TFileStackPickerOptions) => {
                    return {
                        open: pickerOpenMock.mockImplementationOnce(() => {
                            options.onUploadDone?.({
                                filesFailed: [],
                                filesUploaded: [],
                            });
                        }),
                    };
                });

                return {
                    default: Object.assign(
                        () =>
                            Promise.resolve({
                                picker: pickerMock,
                                upload: () => {},
                            }),
                        { __getMocks: () => ({ pickerMock, pickerOpenMock }) },
                    ),
                    basePickerOptions: { fromSources: ['local_file_system', 'url'] },
                };
            });

            appendChild = window.document.body.appendChild;
            Object.assign(window.document.body, { appendChild: vi.fn() });
            createElement = window.document.createElement;
            Object.assign(window.document, {
                createElement: vi.fn().mockReturnValue({ showModal() {}, close() {}, style: {} }),
            });
        });

        afterEach(() => {
            if (!window) return;
            vi.resetAllMocks();
            Object.assign(window.document.body, { appendChild });
            Object.assign(window.document, { createElement });
        });

        test('makes a call to filestack with expected arguments', async () => {
            await uploadFiles({ maxFiles: 2 });
            const { pickerMock, pickerOpenMock } = (filestack as any).__getMocks();
            expect(pickerMock).toBeCalledTimes(1);
            expect(pickerMock.mock.calls[0][0]).toHaveProperty('maxFiles', 2);
            expect(pickerOpenMock).toBeCalledTimes(1);
            expect(pickerOpenMock).toBeCalledWith();
        });
    });
}

// #endregion
